import { Editor } from '@tiptap/core';
import ddt from '@wix/da-ddt';
import { isApiError } from '@wix/da-http-client';
import {
  type PapiDeviation,
  type PapiDaSharedDeviationSubmitUploadDeviation,
  type PapiDaSharedDeviationFetch,
} from '@wix/da-papi-types';
import { requestPuppyFromExtension } from '../utils/requestPuppyFromExtension';
import { getCurrentUser } from '../../components/utils/tiptap';

const MEDIA_FOLDER_NAME = 'Media Uploads';

async function uploadDeviation({
  file,
  editor,
  folderId = undefined,
  folderName = undefined,
  submitPage = false,
}: {
  file: File;
  editor: Editor;
  folderId?: number;
  folderName?: string;
  submitPage?: boolean;
}): Promise<PapiDeviation | undefined> {
  if (!file || !(file instanceof File)) {
    ddt.error('editor-dnd-upload', 'No file specified for deviation upload or file is invalid');
    return;
  }

  const formData = new FormData();
  formData.append('upload_file', file);
  formData.append('folder_name', `${MEDIA_FOLDER_NAME}`);
  formData.append('submit_page', `${submitPage}`);

  const response: PapiDaSharedDeviationSubmitUploadDeviation | false =
    await requestPuppyFromExtension(
      editor,
      {
        method: 'POST',
        url: `/deviation/submit/upload/deviation`,
        data: formData,
      },
      () => {
        if (process.env.NODE_ENV !== 'production') {
          return {
            deviationid: 123,
            status: 'success',
            privateid: 1234,
            stashid: 12345,
            title: 'Dummy',
          };
        }
      }
    );

  if (!response || isApiError(response)) {
    ddt.error('editor-dnd-upload', 'Failed to submit deviation', response);
    return;
  }

  const currentUser = getCurrentUser(editor as any);

  // Fetch deviation
  const deviation: PapiDaSharedDeviationFetch = await requestPuppyFromExtension(
    editor,
    {
      method: 'get',
      url: '/deviation/fetch',
      params: {
        deviationid: response.privateId,
        type: 'art',
        username: currentUser.username,
      },
    },
    () => {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line global-require
        const { deviationsByType } = require('@wix/da-sample-data');
        // eslint-disable-next-line global-require
        const { Chance } = require('chance');
        return new Chance().pickone(deviationsByType.image);
      }
    }
  );

  if (!deviation || isApiError(deviation)) {
    ddt.error('editor-dnd-upload', 'Failed to fetch deviation', deviation);
    return;
  }

  return deviation as PapiDeviation;
}

export default uploadDeviation;
